import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import {
  ReceiptBox,
  ReceiptRow,
  ReceiptLabel,
  ReceiptValue as ReceiptValueBase,
} from '~/modules/matters/modals/shared/retainerModalStyles';
import {
  FooterActions,
  FooterPartyBlock,
  FooterRow,
  FooterSection,
} from '~/modules/matters/MatterCard.styles';

export type RetainerStatusChipStatus = 'Pending' | 'Active' | 'Completed';

export { ReceiptBox, ReceiptRow, ReceiptLabel };

export const ReceiptValue = styled(ReceiptValueBase, {
  shouldForwardProp: (prop) => prop !== '$valueColor',
})<{ $valueColor?: 'error' | 'success' }>(({ theme, $valueColor }) => {
  if ($valueColor === 'error') {
    return { color: theme.palette.red.main };
  }
  if ($valueColor === 'success') {
    return { color: theme.palette.success.dark };
  }
  return {};
});

export const StyledRetainerCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.grey[50],
}));

export const RetainerCardReceiptBox = styled(ReceiptBox)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export const RetainerCardHeaderGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1.25),
}));

/** Primary heading on a retainer card (`retainer.title`). */
export const RetainerCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.35,
}));

/** Optional additional details below the title (`additionalDetails`). */
export const RetainerCardDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

export const RetainerCardFooterSection = styled(FooterSection)({
  borderTop: 'none',
});

export const RetainerCardFooterRow = styled(FooterRow)({});

export const RetainerCardFooterPartyBlock = styled(FooterPartyBlock)({});

export const RetainerCardFooterActions = styled(FooterActions)({});

function getRetainerStatusChipColors(
  theme: Theme,
  status: RetainerStatusChipStatus,
): { bgcolor: string; color: string } {
  switch (status) {
    case 'Active': {
      return {
        bgcolor: theme.palette.success.light,
        color: theme.palette.success.dark,
      };
    }
    case 'Pending': {
      const redMain = theme.palette.red.main;
      return {
        bgcolor: `${redMain}1A`,
        color: theme.palette.red.dark,
      };
    }
    case 'Completed': {
      return {
        bgcolor: theme.palette.grey[700],
        color: theme.palette.common.white,
      };
    }
    default: {
      return {
        bgcolor: theme.palette.grey[700],
        color: theme.palette.common.white,
      };
    }
  }
}

export const RetainerStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: RetainerStatusChipStatus }>(({ theme, status }) => {
  const { bgcolor, color } = getRetainerStatusChipColors(theme, status);
  return {
    bgcolor,
    color,
    fontWeight: 600,
    height: 22,
    '& .MuiChip-label': {
      padding: theme.spacing(0, 1),
      fontSize: theme.typography.caption.fontSize,
    },
  };
});

export const MarkAsClosedLink = styled('button')(({ theme }) => ({
  color: theme.palette.amount.main,
  fontWeight: 600,
  fontSize: theme.typography.caption.fontSize,
  fontFamily: theme.typography.fontFamily,
  textDecoration: 'underline',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius,
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
  '&:disabled': {
    color: theme.palette.action.disabled,
    cursor: 'not-allowed',
  },
}));

export const RetainerCardActionsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100%',
    },
  },
}));
